import { useFileUpload } from "@msuite/ui/file-upload";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type ArticleFormSchema, articleFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { useUpload } from "@/api/files";
import { ArticleForm } from "@/forms/article-form";
import { client } from "@/hono";

/** Props Interface */
interface AddArticleModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
}

export function AddArticleModal({ onClose, isOpen, onComplete }: AddArticleModalProps) {
	/** Hooks */
	const { uploadFn } = useUpload();
	const controller = useFileUpload({ uploadFn });
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(articleFormSchema),
		defaultValues: {
			number: 0,
			autoGenerateNumber: true,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: ArticleFormSchema) {
		const uploadResults = await controller.handleOnFileUpload();
		const response = await client.stock.articles.$post({
			json: {
				...json,
				uploadResults,
			},
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.add_article")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					<ArticleForm imageUploadController={controller} />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
